import JsonView from 'react-json-view';

import { formatDate, StatusField } from '@amfintech/react-admin-ui';
import { createHelpers, createResource } from '@amfintech/refine-react-admin';

import { AuditTrail, Interface, UserType, ActionStatus } from '~/api';

import { resourceNames } from './resource-names';

const { defineFields, defineShowPage, defineCardSection } = createHelpers<AuditTrail>({
  resourceName: resourceNames.auditTrails,
});

export const formatAuditStatus = (status: ActionStatus) => {
  switch (status) {
    case ActionStatus.Success:
      return 'success';
    case ActionStatus.Failure:
      return 'danger';
    case ActionStatus.Warning:
      return 'warn';
    default:
      return 'pending';
  }
};

export const adminAuditResource = createResource({
  identifier: resourceNames.adminAudit,
  name: resourceNames.auditTrails,
  label: 'BackOffice Audit Trails',
  fields: defineFields([
    'id',
    'interface',
    'clientIp',
    'serverIp',
    'userType',
    'userId',
    'username',
    'module',
    'action',
    'channel',
    'timestamp',
    'details',
    'status',
    'relatedEntities',
    'relatedIds',
    'sessionId',
    'environment',
    'appVersion',
    'createdAt',
    'updatedAt',
  ]),
  defaultValues: {
    userType: UserType.Admin,
    interface: Interface.Admin,
  },
  filterControls: {
    username: {
      type: 'text',
      config: {
        label: 'Admin Name',
      },
      operator: 'contains',
    },
    action: {
      type: 'select',
      config: {
        label: 'Action',
        options: [
          { label: 'View', value: 'View' },
          { label: 'List', value: 'List' },
          { label: 'Update', value: 'Update' },
          { label: 'Delete', value: 'Delete' },
          { label: 'Create', value: 'Create' },
        ],
      },
      operator: 'eq',
    },
    module: {
      type: 'select',
      config: {
        label: 'Module',
        options: [
          { label: 'Fund Management', value: 'Fund' },
          { label: 'Account Management', value: 'Account' },
          { label: 'Transaction Generation', value: 'Transaction' },
          { label: 'Order Management', value: 'Order' },
          { label: 'User Management', value: 'User' },
          { label: 'BackOffice User Management', value: 'BackOfficeUser' },
          { label: 'Profile Management', value: 'Profile' },
          { label: 'Announcement Management', value: 'Announcement' },
          { label: 'Payment Management', value: 'Payment' },
          { label: 'Statement Management', value: 'Statement' },
          { label: 'Audit Trail', value: 'AuditTrail' },
          { label: 'Login', value: 'Login' },
          { label: 'Public Holiday', value: 'PublicHoliday' },
        ],
      },
      operator: 'eq',
    },
    timestamp: {
      type: 'daterange',
      config: {
        label: 'Timestamp',
        disabled: {
          after: new Date(),
        },
      },
    },
    status: {
      type: 'select',
      config: {
        label: 'Status',
        options: Object.values(ActionStatus).map((status) => ({ label: status, value: status })),
      },
      operator: 'eq',
    },
  },
  allowCreate: false,
  allowEdit: false,
  allowDelete: false,
  allowDownload: true,
  list: {
    downloadConfig: {
      filenamePrefix: 'AuditTrailReport_Admin',
    },
    filters: {
      collapsible: 'expand-by-default',
      permanent: [
        {
          field: 'interface' as any,
          operator: 'eq',
          value: Interface.Admin,
        },
      ],
    },
  },
  defaultSorter: [{ field: 'timestamp', order: 'desc' }],
  columns: ({ LinkToDetails }) => [
    {
      id: 'timestamp',
      header: 'Timestamp',
      accessorKey: 'timestamp',
      cell: (data) => (
        <LinkToDetails resourceId={data.row.original.id} className="font-semibold">
          {formatDate(data.getValue<string>(), { formatType: 'dateAndTime' })}
        </LinkToDetails>
      ),
    },
    {
      id: 'username',
      header: 'Admin Name',
      accessorKey: 'username',
    },
    {
      id: 'action',
      header: 'Action',
      accessorKey: 'action',
    },
    {
      id: 'module',
      header: 'Module',
      accessorKey: 'module',
    },
    {
      id: 'clientIp',
      header: 'Client IP',
      accessorKey: 'clientIp',
    },
    {
      id: 'serverIp',
      header: 'Server IP',
      accessorKey: 'serverIp',
    },
    {
      id: 'status',
      header: 'Status',
      accessorKey: 'status',
      cell: (data) => (
        <StatusField
          status={formatAuditStatus(data.getValue<ActionStatus>())}
          label={data.getValue<ActionStatus>()}
        />
      ),
    },
  ],
  show: defineShowPage({
    sections: [
      defineCardSection({
        title: 'Admin Audit Details',
        fields: [
          'id',
          'interface',
          'clientIp',
          'serverIp',
          'userType',
          'userId',
          'username',
          'module',
          'action',
          'channel',
          'timestamp',
          'status',
          'sessionId',
          'environment',
          'appVersion',
        ],
      }),
      defineCardSection({
        title: 'Additional Information',
        fields: ['details'],
        collapsible: 'expand-by-default',
        displays: {
          details: {
            label: 'Details',
            render: (data) => (
              <JsonView
                src={data.details}
                displayDataTypes={false}
                displayObjectSize={false}
                enableClipboard={false}
                style={{ padding: '10px' }}
              />
            ),
          },
        },
      }),
    ],
  }),
});
